import React, { useMemo, useState } from 'react';
import {
  COUNTRY_SHAPES,
  MAP_HEIGHT,
  MAP_VIEWBOX,
  MAP_WIDTH,
  VIEW_BOTTOM,
  VIEW_TOP
} from '../../data/worldMap';
import {
  SpeakingLocation,
  allYears,
  groupByCountry,
  speakingLocations
} from '../../data/speakingLocations';

const locationId = (location: SpeakingLocation) => `${location.countryCode}-${location.city}`;

/** Same equirectangular projection the map paths were generated with. */
const project = (lat: number, lon: number) => ({
  x: ((lon + 180) / 360) * MAP_WIDTH,
  y: ((90 - lat) / 180) * MAP_HEIGHT
});

/** Position inside the cropped viewBox, in percent, for HTML overlays. */
const overlayPosition = (lat: number, lon: number) => {
  const { x, y } = project(lat, lon);
  return {
    left: `${(x / MAP_WIDTH) * 100}%`,
    top: `${((y - VIEW_TOP) / (VIEW_BOTTOM - VIEW_TOP)) * 100}%`
  };
};

/**
 * "2019, 2022, 2023, 2024, 2025, 2026" -> "2019, 2022-2026".
 * Runs of three or more consecutive years collapse into a range so that long
 * histories stay on one line; pairs are left as-is.
 */
const formatYears = (years: number[]) => {
  const sorted = Array.from(new Set(years)).sort((a, b) => a - b);
  const parts: string[] = [];

  let runStart = 0;
  for (let i = 1; i <= sorted.length; i++) {
    if (i < sorted.length && sorted[i] === sorted[i - 1] + 1) continue;

    if (i - runStart >= 3) {
      parts.push(`${sorted[runStart]}–${sorted[i - 1]}`);
    } else {
      parts.push(...sorted.slice(runStart, i).map(String));
    }
    runStart = i;
  }

  return parts.join(', ');
};

const SpeakingMap: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const years = useMemo(() => allYears(speakingLocations), []);

  const visible = useMemo(
    () =>
      selectedYear === null
        ? speakingLocations
        : speakingLocations.filter((location) => location.years.includes(selectedYear)),
    [selectedYear]
  );

  const visitedCodes = useMemo(
    () => new Set(visible.map((location) => location.countryCode)),
    [visible]
  );

  const groups = useMemo(() => groupByCountry(visible), [visible]);
  const active = visible.find((location) => locationId(location) === activeId) || null;

  return (
    <section className="speaking-map">
      <h2>Where I have spoken</h2>

      <p className="speaking-map-summary">
        <strong>{visible.length}</strong> {visible.length === 1 ? 'city' : 'cities'} in{' '}
        <strong>{visitedCodes.size}</strong>{' '}
        {visitedCodes.size === 1 ? 'country' : 'countries'}
        {selectedYear !== null && <> in {selectedYear}</>}
      </p>

      {years.length > 0 && (
        <div className="speaking-map-filters" role="group" aria-label="Filter locations by year">
          <button
            type="button"
            className={`year-chip${selectedYear === null ? ' is-active' : ''}`}
            aria-pressed={selectedYear === null}
            onClick={() => {
              setSelectedYear(null);
              setActiveId(null);
            }}
          >
            All years
          </button>
          {years.map((year) => (
            <button
              key={year}
              type="button"
              className={`year-chip${selectedYear === year ? ' is-active' : ''}`}
              aria-pressed={selectedYear === year}
              onClick={() => {
                setSelectedYear(year);
                setActiveId(null);
              }}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      <div className="speaking-map-canvas">
        <svg
          viewBox={MAP_VIEWBOX}
          className="speaking-map-svg"
          role="img"
          aria-label={`World map with pins on ${visible.length} cities where I have spoken`}
        >
          <g className="map-countries">
            {COUNTRY_SHAPES.map((country) => (
              <path
                key={country.code}
                d={country.d}
                className={`map-country${visitedCodes.has(country.code) ? ' is-visited' : ''}`}
              />
            ))}
          </g>

          <g className="map-pins">
            {visible.map((location) => {
              const id = locationId(location);
              const { x, y } = project(location.lat, location.lon);
              const isActive = id === activeId;
              return (
                <g
                  key={id}
                  className={`map-pin${isActive ? ' is-active' : ''}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`${location.city}, ${location.country}${
                    location.years.length ? ` (${formatYears(location.years)})` : ''
                  }`}
                  onMouseEnter={() => setActiveId(id)}
                  onMouseLeave={() => setActiveId((current) => (current === id ? null : current))}
                  onFocus={() => setActiveId(id)}
                  onBlur={() => setActiveId((current) => (current === id ? null : current))}
                >
                  <circle className="map-pin-halo" cx={x} cy={y} r={7} />
                  <circle className="map-pin-dot" cx={x} cy={y} r={3.2} />
                </g>
              );
            })}
          </g>
        </svg>

        {active && (
          <div className="map-tooltip" style={overlayPosition(active.lat, active.lon)} role="status">
            <strong>{active.city}</strong>
            <span>{active.country}</span>
            {active.years.length > 0 && <span className="map-tooltip-years">{formatYears(active.years)}</span>}
          </div>
        )}
      </div>

      <p className="speaking-map-note">On-site talks only &mdash; online presentations are not included.</p>

      <div className="speaking-list">
        {groups.map((group) => (
          <div key={group.countryCode} className="speaking-country">
            <h3>{group.country}</h3>
            <ul>
              {group.cities.map((location) => {
                const id = locationId(location);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={`city-item${id === activeId ? ' is-active' : ''}`}
                      onMouseEnter={() => setActiveId(id)}
                      onMouseLeave={() => setActiveId((current) => (current === id ? null : current))}
                      onFocus={() => setActiveId(id)}
                      onBlur={() => setActiveId((current) => (current === id ? null : current))}
                      onClick={() => setActiveId((current) => (current === id ? null : id))}
                    >
                      <span className="city-name">{location.city}</span>
                      {location.years.length > 0 && (
                        <span className="city-years">{formatYears(location.years)}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SpeakingMap;
